/**
 * WorkflowProgress component for displaying background session workflow phases.
 * Shows a vertical stepper with Planning → Coding → Testing → Reviewing → Finished.
 */
import { cn } from '@/lib/utils'
import type {
  SessionStatus,
  ExecutionCheckpoint,
  PhaseRecord,
  WorkflowPhase,
  WorkflowPhaseStatus,
} from '@/lib/types'
import { WORKFLOW_PHASES } from '@/lib/types'
import { CheckCircle2, Circle, Loader2, XCircle, Clock } from 'lucide-react'

export interface WorkflowProgressProps {
  /** Current session status */
  status: SessionStatus
  /** Checkpoint data with phase history */
  checkpoint?: ExecutionCheckpoint
  /** Error message if session failed */
  errorMessage?: string
  /** Start time for elapsed time calculation */
  startTime?: string
  /** Updated time for terminal status elapsed time calculation */
  updatedAt?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Extended phase type that includes 'finished' pseudo-phase for display.
 */
type DisplayPhase = WorkflowPhase | 'finished'

/**
 * All display phases including 'finished'.
 */
const DISPLAY_PHASES: readonly DisplayPhase[] = [...WORKFLOW_PHASES, 'finished']

/**
 * Human-readable labels for each phase.
 */
const PHASE_LABELS: Record<DisplayPhase, string> = {
  planning: 'Planning',
  coding: 'Coding',
  testing: 'Testing',
  reviewing: 'Reviewing',
  finished: 'Finished',
}

/**
 * Derive the visual status for a given phase based on checkpoint data and session status.
 */
function derivePhaseStatus(
  phase: DisplayPhase,
  checkpoint: ExecutionCheckpoint | undefined,
  sessionStatus: SessionStatus
): WorkflowPhaseStatus {
  // Handle queued status - all pending
  if (sessionStatus === 'Queued') {
    return 'pending'
  }

  // Handle completed session - all phases complete including 'finished'
  if (sessionStatus === 'Completed') {
    return 'completed'
  }

  // Handle failed session
  if (sessionStatus === 'Failed') {
    // The current phase at failure shows error, completed phases stay completed
    if (!checkpoint) return 'pending'
    
    const completedPhases = checkpoint.completedPhases || []
    const isCompleted = completedPhases.some(
      p => p.phase === phase && p.completedAt
    )
    if (isCompleted) return 'completed'
    
    // Current phase at failure shows error
    if (checkpoint.currentPhase === phase) return 'error'
    
    return 'pending'
  }

  // No checkpoint - all pending
  if (!checkpoint) {
    return 'pending'
  }

  // 'finished' is only complete when session is completed (handled above)
  if (phase === 'finished') {
    return 'pending'
  }

  const completedPhases = checkpoint.completedPhases || []

  // Check if phase is completed (has completedAt timestamp)
  const phaseRecord = completedPhases.find(p => p.phase === phase)
  if (phaseRecord?.completedAt) {
    return 'completed'
  }

  // Check if this is the current active phase
  if (checkpoint.currentPhase === phase) {
    return 'active'
  }

  // Otherwise pending
  return 'pending'
}

/**
 * Get the phase record for a given phase from completed phases.
 */
function getPhaseRecord(
  phase: DisplayPhase,
  checkpoint: ExecutionCheckpoint | undefined
): PhaseRecord | undefined {
  if (!checkpoint?.completedPhases || phase === 'finished') {
    return undefined
  }
  return checkpoint.completedPhases.find(p => p.phase === phase)
}

/**
 * Format duration between two timestamps.
 */
function formatDuration(startedAt: string, completedAt: string): string {
  const start = new Date(startedAt).getTime()
  const end = new Date(completedAt).getTime()
  const seconds = Math.floor((end - start) / 1000)

  if (seconds < 60) {
    return `${seconds}s`
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
  }

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

/**
 * Terminal session statuses where elapsed time should freeze.
 */
const TERMINAL_STATUSES: SessionStatus[] = ['Completed', 'Failed', 'Cancelled', 'Timeout']

/**
 * Format elapsed time from start to now or updatedAt for terminal statuses.
 */
function formatElapsedTime(startTime: string, status: SessionStatus, updatedAt?: string): string {
  const start = new Date(startTime).getTime()
  const isTerminal = TERMINAL_STATUSES.includes(status)
  const end = isTerminal && updatedAt ? new Date(updatedAt).getTime() : Date.now()
  const seconds = Math.floor((end - start) / 1000)

  if (seconds < 60) {
    return `${seconds}s`
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (minutes < 60) {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
}

/**
 * Phase icon component based on status.
 */
function PhaseIcon({
  phase,
  status,
}: {
  phase: DisplayPhase
  status: WorkflowPhaseStatus
}) {
  switch (status) {
    case 'completed':
      return (
        <CheckCircle2
          className="w-5 h-5 text-green-500"
          data-testid={`phase-icon-completed-${phase}`}
        />
      )
    case 'active':
      return (
        <Loader2
          className="w-5 h-5 text-primary animate-spin"
          data-testid="phase-icon-active"
        />
      )
    case 'error':
      return (
        <XCircle
          className="w-5 h-5 text-destructive"
          data-testid="phase-icon-error"
        />
      )
    case 'skipped':
      return (
        <Circle
          className="w-5 h-5 text-muted-foreground/50"
          data-testid={`phase-icon-skipped-${phase}`}
        />
      )
    case 'pending':
    default:
      return (
        <Circle
          className="w-5 h-5 text-muted-foreground/30"
          data-testid="phase-icon-pending"
        />
      )
  }
}

/**
 * Single phase row in the workflow progress.
 */
function PhaseRow({
  phase,
  status,
  phaseRecord,
  isLast,
  stepIndex,
  agentName,
  activity,
}: {
  phase: DisplayPhase
  status: WorkflowPhaseStatus
  phaseRecord?: PhaseRecord
  isLast: boolean
  stepIndex?: number
  agentName?: string
  activity?: string
}) {
  const duration =
    phaseRecord?.startedAt && phaseRecord?.completedAt
      ? formatDuration(phaseRecord.startedAt, phaseRecord.completedAt)
      : undefined

  // Show activity if available, otherwise show step/agent
  const showDetails = status === 'active' && (activity || stepIndex !== undefined || agentName)

  return (
    <div
      className="relative flex items-start gap-3"
      data-testid={`phase-row-${phase}`}
      data-active={status === 'active' ? 'true' : undefined}
      data-pending={status === 'pending' ? 'true' : undefined}
      data-completed={status === 'completed' ? 'true' : undefined}
      aria-label={`${PHASE_LABELS[phase]}: ${status}`}
    >
      {/* Vertical connector line */}
      {!isLast && (
        <div
          className={cn(
            'absolute left-[9px] top-6 w-0.5',
            showDetails ? 'h-10' : 'h-6',
            status === 'completed' ? 'bg-green-500/50' : 'bg-muted-foreground/20'
          )}
        />
      )}

      {/* Icon */}
      <div className="flex-shrink-0 z-10 bg-background">
        <PhaseIcon phase={phase} status={status} />
      </div>

      {/* Label, duration, and active phase details */}
      <div className="flex-1 min-h-[24px]">
        <div className="flex justify-between items-center">
          <span
            className={cn(
              'text-sm font-medium',
              status === 'active' && 'text-primary',
              status === 'completed' && 'text-foreground',
              status === 'pending' && 'text-muted-foreground',
              status === 'error' && 'text-destructive'
            )}
          >
            {PHASE_LABELS[phase]}
          </span>

          {duration && (
            <span
              className="text-xs text-muted-foreground"
              data-testid={`phase-duration-${phase}`}
            >
              {duration}
            </span>
          )}
        </div>
        {/* Show activity or step/agent details for active phase */}
        {showDetails && (
          <div className="text-xs text-muted-foreground mt-0.5" data-testid="active-phase-details">
            {activity ? (
              <span>{activity}</span>
            ) : (
              <>
                {stepIndex !== undefined && <span>Step {stepIndex}</span>}
                {stepIndex !== undefined && agentName && <span> • </span>}
                {agentName && <span>{agentName}</span>}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * WorkflowProgress displays a vertical stepper showing session execution phases.
 *
 * @example
 * ```tsx
 * <WorkflowProgress
 *   status="Running"
 *   checkpoint={sessionMetadata.checkpoint}
 * />
 * ```
 */
export function WorkflowProgress({
  status,
  checkpoint,
  errorMessage: _errorMessage,
  startTime,
  updatedAt,
  className,
}: WorkflowProgressProps) {
  // Show queued state
  if (status === 'Queued') {
    return (
      <div className={cn('space-y-3', className)}>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="w-4 h-4" />
          <span className="text-sm">Queued</span>
        </div>
        <div className="space-y-4">
          {DISPLAY_PHASES.map((phase, index) => (
            <PhaseRow
              key={phase}
              phase={phase}
              status="pending"
              isLast={index === DISPLAY_PHASES.length - 1}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Phase steps */}
      <div className="space-y-4">
        {DISPLAY_PHASES.map((phase, index) => {
          const phaseStatus = derivePhaseStatus(phase, checkpoint, status)
          const phaseRecord = getPhaseRecord(phase, checkpoint)
          const isActivePhase = phaseStatus === 'active'

          return (
            <PhaseRow
              key={phase}
              phase={phase}
              status={phaseStatus}
              phaseRecord={phaseRecord}
              isLast={index === DISPLAY_PHASES.length - 1}
              stepIndex={isActivePhase ? checkpoint?.stepIndex : undefined}
              agentName={isActivePhase ? checkpoint?.currentAgentName : undefined}
              activity={isActivePhase ? checkpoint?.currentActivity : undefined}
            />
          )
        })}
      </div>

      {/* Elapsed time footer */}
      {startTime && (
        <div
          className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t border-border"
          data-testid="elapsed-time"
        >
          <Clock className="w-3 h-3" />
          <span>Elapsed: {formatElapsedTime(startTime, status, updatedAt)}</span>
        </div>
      )}
    </div>
  )
}

export default WorkflowProgress
